import React, { useState, useEffect, useRef } from 'react';
import api from './api';

/* ── useSecureBlob ────────────────────────────────────────────────────────────
 * Fetches a file via authenticated API and returns a revokable blob URL.
 * Returns null until loaded (or on error).
 */
function useSecureBlob(apiPath) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [unsafe, setUnsafe] = useState(false);
  const urlRef = useRef(null);
  useEffect(() => {
    if (!apiPath) { setUnsafe(false); return; }
    let cancelled = false;
    api.get(apiPath, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        if (res.headers?.['x-file-unsafe'] === '1') setUnsafe(true);
        else setUnsafe(false);
        const u = URL.createObjectURL(res.data);
        urlRef.current = u;
        setBlobUrl(u);
      })
      .catch(() => setBlobUrl(null));
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [apiPath]);
  return { blobUrl, unsafe };
}

const BASE = process.env.REACT_APP_URL;

// ── helpers ──────────────────────────────────────────────────────────────────
function fileIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp','bmp','tiff'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['mp4','mov','avi','mkv'].includes(ext)) return '🎬';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  return '📎';
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── section card ──────────────────────────────────────────────────────────────
function SectionCard({ icon, title, children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: 18, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 18px', borderBottom: '1px solid #f3f4f6',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{title}</span>
      </div>
      <div style={{ padding: '14px 18px' }}>{children}</div>
    </div>
  );
}

// ── file row ──────────────────────────────────────────────────────────────────
function FileRow({ file, section, patientCard, onDelete }) {
  const [deleting, setDeleting] = useState(false);

  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|tiff)$/i.test(file.filename);
  const apiPath = `/patient-files/${patientCard}/${section}/${file.filename}`;
  const { blobUrl, unsafe } = useSecureBlob(isImage ? apiPath : null); // load thumbnail only for images

  async function handleDelete() {
    if (!window.confirm(`Delete "${file.filename}"? This cannot be undone.`)) return;
    setDeleting(true);
    try { await onDelete(file.filename); } finally { setDeleting(false); }
  }

  function handleOpen() {
    api.get(apiPath, { responseType: 'blob' }).then(res => {
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      if (!win) alert('Pop-up blocked. Please allow pop-ups for this site.');
    }).catch(() => alert('Could not open file.'));
  }

  // Clean display name: strip the timestamp prefix
  const displayName = (file.filename || '').replace(/^\d+-/, '');

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px', borderRadius: 8,
      border: unsafe ? '3px solid #dc2626' : '1px solid #f0f0f0',
      marginBottom: 8, background: unsafe ? '#fef2f2' : '#fafafa',
      position: 'relative',
    }}>
      {unsafe && (
        <div style={{ position: 'absolute', top: -9, left: 10, background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4 }}>
          Not encrypted — re-upload this file
        </div>
      )}
      {isImage && blobUrl ? (
        <img src={blobUrl} alt={file.filename} style={{
          width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: unsafe ? '2px solid #dc2626' : '1px solid #e5e7eb', flexShrink: 0,
        }} />
      ) : (
        <span style={{ fontSize: 24, flexShrink: 0 }}>{isImage && !blobUrl ? '⏳' : fileIcon(file.filename)}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
          {formatBytes(file.size)} · {formatDate(file.uploadedAt)}
        </div>
      </div>
      <button
        onClick={handleOpen}
        style={{
          padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db',
          background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', flexShrink: 0,
        }}
      >
        Open
      </button>
      <button
        onClick={handleDelete}
        disabled={deleting}
        style={{
          padding: '5px 10px', borderRadius: 6, border: '1px solid #fca5a5',
          background: '#fff5f5', color: '#dc2626', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', flexShrink: 0,
        }}
      >
        {deleting ? '…' : 'Delete'}
      </button>
    </div>
  );
}

// ── upload button ─────────────────────────────────────────────────────────────
function UploadButton({ accept, onUpload, uploading, label }) {
  const inputRef = useRef(null);

  function handleChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    onUpload(file);
    e.target.value = '';
  }

  return (
    <>
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }} onChange={handleChange} />
      <button
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={uploading}
        style={{
          padding: '8px 18px', borderRadius: 8,
          border: '1.5px dashed #2563eb', background: '#eff6ff',
          color: '#1d4ed8', fontSize: 13, fontWeight: 600,
          cursor: uploading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        {uploading ? '⏳ Uploading…' : `⬆ ${label}`}
      </button>
    </>
  );
}

// ── main component ────────────────────────────────────────────────────────────
export default function PatientFiles({ patientCard, patientPic }) {
  const [photo, setPhoto]               = useState(patientPic || null);
  const [reports, setReports]           = useState([]);
  const [resources, setResources]       = useState([]);
  const [uploading, setUploading]       = useState({ photo: false, report: false, resource: false });
  const [uploadSuccess, setUploadSuccess] = useState({ photo: false, report: false, resource: false });
  const [loadingFiles, setLoadingFiles] = useState(true);

  // Derive the base domain for absolute URLs (file serve path is relative)
  const serverBase = BASE ? BASE.replace('/api', '').replace(':5000/api', ':5000') : '';

  useEffect(() => {
    if (!patientCard) return;
    Promise.all([
      api.get(`${BASE}/patient-files/${patientCard}/reports`).catch(() => ({ data: [] })),
      api.get(`${BASE}/patient-files/${patientCard}/resources`).catch(() => ({ data: [] })),
    ]).then(([rRes, resRes]) => {
      setReports(rRes.data || []);
      setResources(resRes.data || []);
    }).finally(() => setLoadingFiles(false));
  }, [patientCard]);

  // ── photo upload ──────────────────────────────────────────────────────────
  async function handlePhotoUpload(file) {
    setUploading(u => ({ ...u, photo: true }));
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await api.post(`${BASE}/patient-files/${patientCard}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhoto(res.data.url);
      setUploadSuccess(u => ({ ...u, photo: true }));
      setTimeout(() => setUploadSuccess(u => ({ ...u, photo: false })), 4000);
    } catch (err) {
      alert('Photo upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(u => ({ ...u, photo: false }));
    }
  }

  async function handlePhotoDelete() {
    if (!window.confirm('Remove profile photo?')) return;
    try {
      await api.delete(`${BASE}/patient-files/${patientCard}/photo`);
      setPhoto(null);
    } catch (err) {
      alert('Failed to delete photo');
    }
  }

  // ── reports upload / delete ───────────────────────────────────────────────
  async function handleReportUpload(file) {
    setUploading(u => ({ ...u, report: true }));
    try {
      const fd = new FormData();
      fd.append('report', file);
      const res = await api.post(`${BASE}/patient-files/${patientCard}/reports`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReports(prev => [res.data, ...prev]);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(u => ({ ...u, report: false }));
    }
  }

  async function handleReportDelete(filename) {
    await api.delete(`${BASE}/patient-files/${patientCard}/reports/${filename}`);
    setReports(prev => prev.filter(f => f.filename !== filename));
  }

  // ── resources upload / delete ─────────────────────────────────────────────
  async function handleResourceUpload(file) {
    setUploading(u => ({ ...u, resource: true }));
    try {
      const fd = new FormData();
      fd.append('resource', file);
      const res = await api.post(`${BASE}/patient-files/${patientCard}/resources`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResources(prev => [res.data, ...prev]);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(u => ({ ...u, resource: false }));
    }
  }

  async function handleResourceDelete(filename) {
    await api.delete(`${BASE}/patient-files/${patientCard}/resources/${filename}`);
    setResources(prev => prev.filter(f => f.filename !== filename));
  }

  if (!patientCard) return <div style={{ padding: 24, color: '#888' }}>No patient selected.</div>;

  return (
    <div style={{ padding: '4px 0' }}>

      {/* ── Profile Photo ── */}
      <SectionCard icon="👤" title="Profile Photo">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {photo ? (
            <div style={{ position: 'relative' }}>
              <img
                src={photo.startsWith('/pfiles') ? `${serverBase}${photo}` : photo}
                alt="Patient"
                style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #e5e7eb' }}
              />
              <button
                onClick={handlePhotoDelete}
                style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#ef4444', color: '#fff', border: 'none',
                  fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >×</button>
            </div>
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: '#f3f4f6', border: '2px dashed #d1d5db',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, color: '#9ca3af',
            }}>👤</div>
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UploadButton
                accept="image/*"
                onUpload={handlePhotoUpload}
                uploading={uploading.photo}
                label="Upload Photo from File"
              />
              {uploadSuccess.photo && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: '#dcfce7', color: '#16a34a', fontWeight: 700,
                  fontSize: 12, padding: '4px 10px', borderRadius: 8,
                  animation: 'fadeIn 0.3s ease',
                }}>
                  &#10003; Uploaded
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
              Stores on external HDD · Max 5 MB · JPG / PNG / WEBP
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ── Lab Reports / Scans ── */}
      <SectionCard icon="🧾" title={`Lab Reports & Scans${reports.length > 0 ? ` (${reports.length})` : ''}`}>
        <div style={{ marginBottom: 12 }}>
          <UploadButton
            accept=".pdf,image/*"
            onUpload={handleReportUpload}
            uploading={uploading.report}
            label="Upload Report / Scan"
          />
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
            PDF · JPG · PNG · Max 20 MB per file · stored on external HDD
          </div>
        </div>
        {loadingFiles ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>
        ) : reports.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>No reports uploaded yet.</div>
        ) : (
          reports.map(f => (
            <FileRow key={f.filename} file={f} section="reports" patientCard={patientCard} onDelete={handleReportDelete} />
          ))
        )}
      </SectionCard>

      {/* ── Patient Resources ── */}
      <SectionCard icon="📚" title={`Patient Resources${resources.length > 0 ? ` (${resources.length})` : ''}`}>
        <UploadButton
          accept=".pdf,.doc,.docx,.xls,.xlsx,.mp4,.mov,image/*"
          onUpload={handleResourceUpload}
          uploading={uploading.resource}
          label="Upload Resource"
        />
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12 }}>
          PDF · DOC · XLS · Videos · Max 50 MB per file · stored on external HDD
        </div>

        {loadingFiles ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>
        ) : resources.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>No resources uploaded yet.</div>
        ) : (
          resources.map(f => (
            <FileRow key={f.filename} file={f} section="resources" patientCard={patientCard}
              onDelete={handleResourceDelete} />
          ))
        )}
      </SectionCard>

      {/* ── Storage info ── */}
      <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', paddingBottom: 8 }}>
        Storage path is configured via <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4 }}>UPLOADS_BASE_PATH</code> in server .env
      </div>

    </div>
  );
}
